import { ShopifyConnect } from '@/features/kurulum/ShopifyConnect';
import { useState } from "react";

import { ApiError } from "@/api/client";
import { useRegisterWebhooksMutation } from "@/api/ikas";
import {
  useTenantCredentialsQuery,
  useTenantSettingsQuery,
  useUpdateTenantCredentialsMutation,
} from "@/api/settings";
import { useAuth } from "@/auth";
import { Button } from "@/components/ui";
import { ConnectionTestStep } from "@/features/kurulum/ConnectionTestStep";
import { IdeasoftConnect } from "@/features/kurulum/IdeasoftConnect";
import { IkasConnect } from "@/features/kurulum/IkasConnect";
import { SyncStep } from "@/features/kurulum/SyncStep";
import { TicimaxCredentialsForm } from "@/features/kurulum/TicimaxCredentialsForm";
import { t, type TrKey } from "@/i18n";

type StepId = "platform" | "credentials" | "test" | "sync";
const STEPS: StepId[] = ["platform", "credentials", "test", "sync"];
const STEP_LABEL: Record<StepId, TrKey> = {
  platform: "kurulum.steps.platform",
  credentials: "kurulum.steps.credentials",
  test: "kurulum.steps.test",
  sync: "kurulum.steps.sync",
};

const PLATFORM_LABEL: Record<string, TrKey> = {
  ikas: "kurulum.platform.ikas",
  ideasoft: "kurulum.platform.ideasoft",
  shopify: "kurulum.platform.shopify",
  ticimax: "kurulum.platform.ticimax",
};

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.message) return error.message;
  return fallback;
}

interface KurulumProps {
  /** App.tsx `/kurulum/ikas/tamam` ve `/kurulum/ideasoft/tamam` rotalarindan
   * "credentials" ile acilir (bkz. asagidaki dosya notu) — varsayilan
   * "platform" (dogrudan /kurulum). */
  initialStep?: StepId;
}

/**
 * /kurulum — tenant_admin adimli kurulum sihirbazi (gorev talimati madde 3):
 * Platform bilgisi → Kimlik (Ticimax/ikas/IdeaSoft) → Bağlantı testi → İlk
 * senkron.
 *
 * ikas/IdeaSoft OAuth donusu backend'de sirasiyla `/kurulum/ikas/tamam` ve
 * `/kurulum/ideasoft/tamam`'a yonlendirir — App.tsx bu yollari
 * `initialStep="credentials"` ile BU bilesene baglar, boylece kullanici geri
 * donduğunde dogrudan "Kimlik bilgileri" adimindaki baglanti onayini gorur.
 * `IkasConnect`/`IdeasoftConnect` ayrica HER YUKLEMEDE kendi bagli-mi
 * durumunu kendiliginden dogrular.
 */
export function Kurulum({ initialStep = "platform" }: KurulumProps = {}) {
  const { principal } = useAuth();
  const platform = principal?.active_tenant?.platform;

  const [step, setStep] = useState<StepId>(initialStep);
  const settingsQuery = useTenantSettingsQuery();
  const credentialsQuery = useTenantCredentialsQuery();
  const updateCredentials = useUpdateTenantCredentialsMutation();
  const registerWebhooks = useRegisterWebhooksMutation();
  const [credSuccess, setCredSuccess] = useState(false);
  const [webhookSuccess, setWebhookSuccess] = useState(false);

  const stepIndex = STEPS.indexOf(step);

  function goNext() {
    setStep(STEPS[Math.min(stepIndex + 1, STEPS.length - 1)]);
  }
  function goBack() {
    setStep(STEPS[Math.max(stepIndex - 1, 0)]);
  }

  return (
    <section className="mx-auto max-w-2xl space-y-6">
      <h1 className="font-display text-xl font-bold text-ink">{t("kurulum.title")}</h1>

      <ol className="flex flex-wrap items-center gap-x-1 gap-y-3 overflow-x-auto">
        {STEPS.map((s, i) => {
          const done = i < stepIndex;
          const active = s === step;
          return (
            <li key={s} aria-current={active ? "step" : undefined} className="flex items-center gap-2">
              <span
                className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-bold ${
                  active
                    ? "bg-brand-primary text-white"
                    : done
                      ? "bg-green text-white"
                      : "border-[1.5px] border-line bg-card text-muted"
                }`}
              >
                {done ? "✓" : i + 1}
              </span>
              <span className={`whitespace-nowrap text-[13px] font-semibold ${active ? "text-ink" : "text-muted"}`}>
                {t(STEP_LABEL[s])}
              </span>
              {i < STEPS.length - 1 && <span className="mx-1 h-px w-6 shrink-0 bg-line sm:w-10" aria-hidden="true" />}
            </li>
          );
        })}
      </ol>

      <div className="card">
        {step === "platform" && (
          <div className="space-y-3">
            <p className="text-sm text-muted">{t("kurulum.platform.hint")}</p>
            <dl className="grid grid-cols-2 gap-y-2 text-sm">
              <dt className="text-muted">{t("kurulum.platform.name")}</dt>
              <dd className="text-ink">{principal?.active_tenant?.name ?? "—"}</dd>
              <dt className="text-muted">{t("kurulum.platform.title")}</dt>
              <dd className="text-ink">{t(platform ? (PLATFORM_LABEL[platform] ?? "kurulum.platform.ticimax") : "kurulum.platform.ticimax")}</dd>
            </dl>
          </div>
        )}

        {step === "credentials" && (
          <>
            {platform === "ikas" && (
              <IkasConnect
                storeName={String(settingsQuery.data?.["ikas.store_name"] ?? "")}
                onRegisterWebhooks={() => {
                  setWebhookSuccess(false);
                  registerWebhooks.mutate(undefined, { onSuccess: () => setWebhookSuccess(true) });
                }}
                isRegistering={registerWebhooks.isPending}
                registerError={registerWebhooks.isError ? errorMessage(registerWebhooks.error, t("kurulum.ikas.webhooks_error")) : null}
                registerSuccess={webhookSuccess}
              />
            )}
            {platform === "ideasoft" && (
              <IdeasoftConnect
                existing={credentialsQuery.data ?? []}
                isSaving={updateCredentials.isPending}
                success={credSuccess}
                error={updateCredentials.isError ? errorMessage(updateCredentials.error, t("kurulum.ideasoft.save_error")) : null}
                onSave={(values) => {
                  setCredSuccess(false);
                  updateCredentials.mutate(values, { onSuccess: () => setCredSuccess(true) });
                }}
              />
            )}
            {platform === "shopify" && <ShopifyConnect isSaving={updateCredentials.isPending} success={credSuccess}
              error={updateCredentials.isError ? errorMessage(updateCredentials.error, 'Bağlantı bilgisi kaydedilemedi.') : null}
              onSave={values => { setCredSuccess(false); updateCredentials.mutate(values,{onSuccess:()=>setCredSuccess(true)}); }} />}
            {platform === "ticimax" && (
              <TicimaxCredentialsForm
                existing={credentialsQuery.data ?? []}
                isSaving={updateCredentials.isPending}
                success={credSuccess}
                error={updateCredentials.isError ? errorMessage(updateCredentials.error, t("kurulum.ticimax.save_error")) : null}
                onSave={(values) => {
                  setCredSuccess(false);
                  updateCredentials.mutate(values, { onSuccess: () => setCredSuccess(true) });
                }}
              />
            )}
          </>
        )}

        {step === "test" && <ConnectionTestStep />}

        {step === "sync" && <SyncStep />}
      </div>

      <div className="flex justify-between">
        <Button type="button" variant="ghost" onClick={goBack} disabled={stepIndex === 0}>
          {t("kurulum.back")}
        </Button>
        <Button type="button" variant="primary" onClick={goNext} disabled={stepIndex === STEPS.length - 1}>
          {t("kurulum.next")}
        </Button>
      </div>
    </section>
  );
}
